/**
 * Shared, platform-agnostic half of Skeleton: dimension/colour resolution and
 * the invisible children slot.
 *
 * The animation itself is platform-specific — web drives it with CSS keyframes
 * (`Skeleton.web.tsx`) and native with Reanimated (`Skeleton.native.tsx`) — so
 * everything either platform needs is resolved once here and handed over as
 * {@link SkeletonSharedState}.
 */
import { useMemo, type FC, type ReactNode } from "react";
import type { DimensionValue, ViewStyle } from "react-native";
import "../../theme"; // module augmentation for react-native-unistyles types
import { useUnistyles } from "react-native-unistyles";
import { View } from "../View";
import { textStyleSpecs, type TextSize } from "../Text";
import { useSkeletonAnimation } from "./SkeletonGroup";
import type { SkeletonAnimation, SkeletonProps, SkeletonVariant } from "./Skeleton.types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Text scale the placeholder is measured against when none is given. */
const DEFAULT_TEXT_STYLE: TextSize = "sm/regular";

/** Avatar-sized default for `variant="circular"`, matching the icon buttons. */
const DEFAULT_CIRCULAR_SIZE = 40;

/**
 * Fraction of the line height a text placeholder fills. Matches MUI's
 * `scale(1, 0.6)` on its text skeleton: a full-height bar reads as a solid
 * block rather than a line of prose.
 */
const TEXT_HEIGHT_RATIO = 0.6;

/** Radius large enough to fully round any placeholder we render. */
const CIRCULAR_RADIUS = 9999;

// ---------------------------------------------------------------------------
// Text metrics
// ---------------------------------------------------------------------------

interface SkeletonTextMetrics {
  /**
   * Height the placeholder occupies — the full line height, so a stack of text
   * placeholders is exactly as tall as the copy that will replace it.
   */
  readonly lineHeight: number;
  /**
   * Vertical scale applied to paint the bar shorter than the line box it
   * occupies. A transform rather than a smaller height, so the layout keeps the
   * text's own rhythm (this is how MUI's text skeleton works).
   */
  readonly scaleY: number;
}

function getSkeletonTextMetrics(textStyle: TextSize): SkeletonTextMetrics {
  const { lineHeight } = textStyleSpecs[textStyle];
  return { lineHeight, scaleY: TEXT_HEIGHT_RATIO };
}

// ---------------------------------------------------------------------------
// Accessibility
// ---------------------------------------------------------------------------

/**
 * Two concrete shapes rather than one shape of optional props, so the object
 * can be spread onto a `View` under `exactOptionalPropertyTypes`.
 */
export type SkeletonAccessibilityProps =
  | {
      readonly accessible: false;
      readonly accessibilityElementsHidden: true;
      readonly importantForAccessibility: "no-hide-descendants";
      readonly "aria-hidden": true;
    }
  | {
      readonly accessible: true;
      readonly accessibilityRole: "progressbar";
      readonly accessibilityLabel: string;
      readonly accessibilityState: { readonly busy: true };
    };

function resolveAccessibilityProps(accessibilityLabel: string | undefined): SkeletonAccessibilityProps {
  if (accessibilityLabel === undefined) {
    return {
      accessible: false,
      accessibilityElementsHidden: true,
      importantForAccessibility: "no-hide-descendants",
      "aria-hidden": true,
    };
  }

  return {
    accessible: true,
    accessibilityRole: "progressbar",
    accessibilityLabel,
    accessibilityState: { busy: true },
  };
}

// ---------------------------------------------------------------------------
// Shared state
// ---------------------------------------------------------------------------

export interface SkeletonSharedState {
  /** Animation after applying the prop, the enclosing group, and the default. */
  readonly animation: SkeletonAnimation;
  /** Base style for the placeholder surface, before the caller's `style`. */
  readonly containerStyle: ViewStyle;
  /** Colour of the sheen that travels across a `wave` placeholder. */
  readonly highlightColor: string;
  readonly children: ReactNode | undefined;
  /** Whether `children` will render, and therefore drive the placeholder size. */
  readonly hasChildren: boolean;
  readonly accessibilityProps: SkeletonAccessibilityProps;
  readonly testID: string | undefined;
}

function resolveRadius(variant: SkeletonVariant, spacing: { readonly xs: number; readonly md: number }): number {
  switch (variant) {
    case "text":
      return spacing.xs;
    case "rounded":
      return spacing.md;
    case "circular":
      return CIRCULAR_RADIUS;
    case "rectangular":
      return 0;
  }
}

function resolveWidth(
  variant: SkeletonVariant,
  width: DimensionValue | undefined,
  hasChildren: boolean,
): DimensionValue | undefined {
  if (width !== undefined) return width;
  if (hasChildren) return undefined;
  return variant === "circular" ? DEFAULT_CIRCULAR_SIZE : "100%";
}

function resolveHeight(
  variant: SkeletonVariant,
  height: DimensionValue | undefined,
  width: DimensionValue | undefined,
  metrics: SkeletonTextMetrics,
  hasChildren: boolean,
): DimensionValue | undefined {
  if (height !== undefined) return height;
  if (hasChildren) return undefined;
  // A circle needs a height to exist at all, so mirror the width when we can.
  if (variant === "circular") return typeof width === "number" ? width : DEFAULT_CIRCULAR_SIZE;
  return metrics.lineHeight;
}

export function useSkeletonSharedState({
  variant = "text",
  animation,
  width,
  height,
  textStyle = DEFAULT_TEXT_STYLE,
  children,
  accessibilityLabel,
  testID,
}: SkeletonProps): SkeletonSharedState {
  const { theme } = useUnistyles();
  const resolvedAnimation = useSkeletonAnimation(animation);

  const backgroundColor = theme.colors.skeleton;
  // The sheen has to read as light-on-dark in dark mode and light-on-light in
  // light mode, so it is a translucent white in both — only the strength differs.
  const highlightColor = theme.currentTheme === "dark" ? theme.colors.white[100] : theme.colors.white[700];

  const hasChildren = children !== undefined && children !== null && children !== false;
  const spacing = theme.spacing;

  const containerStyle = useMemo((): ViewStyle => {
    const metrics = getSkeletonTextMetrics(textStyle);
    const resolvedWidth = resolveWidth(variant, width, hasChildren);
    // Only an auto-sized text bar gets squashed — an explicit height (or one
    // inferred from children) is the height the caller expects to see painted.
    const paintsShorterThanLineBox = variant === "text" && height === undefined && !hasChildren;

    return {
      backgroundColor,
      borderRadius: resolveRadius(variant, spacing),
      width: resolvedWidth,
      height: resolveHeight(variant, height, resolvedWidth, metrics, hasChildren),
      // The sheen is a child that slides beyond both edges; without this it
      // would paint over neighbouring content.
      overflow: resolvedAnimation === "wave" ? "hidden" : "visible",
      ...(paintsShorterThanLineBox ? { transform: [{ scaleY: metrics.scaleY }] } : {}),
      // Children-sized placeholders must not stretch to fill a flex parent,
      // otherwise the inferred width is lost.
      ...(hasChildren ? { alignSelf: "flex-start" } : {}),
    };
  }, [variant, width, height, hasChildren, textStyle, backgroundColor, spacing, resolvedAnimation]);

  return {
    animation: resolvedAnimation,
    containerStyle,
    highlightColor,
    children,
    hasChildren,
    accessibilityProps: resolveAccessibilityProps(accessibilityLabel),
    testID,
  };
}

// ---------------------------------------------------------------------------
// Children slot
// ---------------------------------------------------------------------------

export interface SkeletonChildrenSlotProps {
  readonly children: ReactNode;
}

/**
 * Lays children out to establish the placeholder's size without painting them.
 *
 * React Native has no `visibility: hidden`, so this uses `opacity: 0`. Unlike
 * `visibility`, that alone still takes part in hit-testing, so the slot also
 * disables pointer events — otherwise a child used purely for measurement
 * could swallow presses aimed at whatever sits under the placeholder. Children
 * are still expected to be inert content; the slot cannot take an interactive
 * child out of the tab order.
 */
const SkeletonChildrenSlot: FC<SkeletonChildrenSlotProps> = ({ children }) => (
  <View style={hiddenChildrenStyle} pointerEvents="none">
    {children}
  </View>
);

SkeletonChildrenSlot.displayName = "SkeletonChildrenSlot";

const hiddenChildrenStyle: ViewStyle = { opacity: 0 };

export { SkeletonChildrenSlot };
